import React, { useState, useEffect } from 'react';
import { EyeIcon, ComputerDesktopIcon, DocumentDuplicateIcon, ArrowPathIcon, CursorArrowRaysIcon } from '@heroicons/react/24/outline';
import { useCandidateT } from '../../i18n/candidate';

/**
 * ProctoringIndicator - Shows real-time proctoring status
 * Displays fullscreen status, recent events, and warnings
 * Only visible when Ctrl+Shift+V is pressed
 */
export const ProctoringIndicator = ({ 
  isFullscreen, 
  proctoringEvents, 
  shiftClickCount,
  rightClickCount,
  isVisible = true 
}) => {
  const [showProctoring, setShowProctoring] = useState(false);
  const t = useCandidateT();

  // Handle Ctrl+Shift+V to toggle proctoring visibility
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Ctrl+Shift+V combination
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        e.stopPropagation();
        setShowProctoring(prev => !prev);
        console.log('🔍 Proctoring panel toggled:', !showProctoring);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showProctoring]);

  if (!isVisible) return null;

  // Show small hint when proctoring is hidden
  if (!showProctoring) {
    return (
      <div className="fixed bottom-4 right-4 z-40">
        
      </div>
    );
  }

  const recentEvents = proctoringEvents.slice(-5).reverse(); // Last 5 events

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'COPY_DETECTED':
      case 'KEYBOARD_COPY_DETECTED':
        return <DocumentDuplicateIcon className="w-4 h-4 text-blue-500" />;
      case 'PASTE_DETECTED':
      case 'KEYBOARD_PASTE_DETECTED':
        return <DocumentDuplicateIcon className="w-4 h-4 text-blue-500" />;
      case 'TAB_SWITCH_DETECTED':
      case 'ALT_TAB_DETECTED':
        return <ArrowPathIcon className="w-4 h-4 text-orange-500" />;
      case 'WINDOW_BLUR_DETECTED':
        return <ComputerDesktopIcon className="w-4 h-4 text-orange-500" />;
      case 'FULLSCREEN_EXITED':
        return <ComputerDesktopIcon className="w-4 h-4 text-red-500" />;
      case 'RIGHT_CLICK_DETECTED':
        return <CursorArrowRaysIcon className="w-4 h-4 text-red-500" />;
      case 'SHIFT_CLICK_DETECTED':
      case 'SHIFT_KEY_DETECTED':
        return <ArrowPathIcon className="w-4 h-4 text-yellow-500" />;
      default:
        return <EyeIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getEventMessage = (eventType) => {
    switch (eventType) {
      case 'COPY_DETECTED':
        return 'Copy detected';
      case 'PASTE_DETECTED':
        return 'Paste detected';
      case 'KEYBOARD_COPY_DETECTED':
        return 'Keyboard copy (Ctrl+C)';
      case 'KEYBOARD_PASTE_DETECTED':
        return 'Keyboard paste (Ctrl+V)';
      case 'KEYBOARD_CUT_DETECTED':
        return 'Keyboard cut (Ctrl+X)';
      case 'CUT_DETECTED':
        return 'Cut detected';
      case 'TAB_SWITCH_DETECTED':
        return 'Tab switched';
      case 'TAB_RETURN_DETECTED':
        return 'Tab returned';
      case 'ALT_TAB_DETECTED':
        return 'Alt+Tab detected';
      case 'WINDOW_BLUR_DETECTED':
        return 'Window lost focus';
      case 'WINDOW_FOCUS_DETECTED':
        return 'Window focused';
      case 'FULLSCREEN_ENTERED':
        return 'Fullscreen entered';
      case 'FULLSCREEN_EXITED':
        return 'Fullscreen exited';
      case 'RIGHT_CLICK_DETECTED':
        return 'Right-click blocked';
      case 'SHIFT_CLICK_DETECTED':
        return 'Shift+Click detected';
      case 'SHIFT_KEY_DETECTED':
        return 'Shift key pressed';
      default:
        return 'Activity detected';
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-3 max-w-sm">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
        <EyeIcon className="w-4 h-4 text-gray-600" />
        <span className="text-xs font-semibold text-gray-700">{t('Proctoring Active')}</span>
      </div>

      {/* Fullscreen Status */}
      <div className="mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${isFullscreen ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className="text-xs text-gray-700">
            {isFullscreen ? t('Fullscreen Active') : t('Fullscreen Required')}
          </span>
        </div>
      </div>

      {/* Activity Counts */}
      <div className="mb-3 grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center gap-1">
          <CursorArrowRaysIcon className="w-3 h-3 text-red-500" />
          <span className="text-gray-600">{t('Right-clicks: {count}', { count: rightClickCount })}</span>
        </div>
        <div className="flex items-center gap-1">
          <ArrowPathIcon className="w-3 h-3 text-yellow-500" />
          <span className="text-gray-600">{t('Shift-clicks: {count}', { count: shiftClickCount })}</span>
        </div>
      </div>

      {/* Recent Events */}
      {recentEvents.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-700 mb-2">{t('Recent Activity:')}</p>
          <div className="space-y-1 max-h-24 overflow-y-auto">
            {recentEvents.map((event, index) => (
              <div key={event.id} className="flex items-center gap-2 text-xs">
                {getEventIcon(event.eventType)}
                <span className="text-gray-600">{t(getEventMessage(event.eventType))}</span>
                <span className="text-gray-400 text-xs">
                  {new Date(event.timestamp).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Warning Message */}
      <div className="mt-3 pt-2 border-t border-gray-100">
        <p className="text-xs text-amber-600">
          ⚠️ {t('All activities are being monitored and recorded')}
        </p>
      </div>
    </div>
  );
};
